// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import Login from './Login';
import Dashboard from './Dashboard';
import Lancamentos from './modules/lancamentos/pages/Lancamentos';
import Relatorios from './modules/relatorios/pages/Relatorios';
import RelatoriosAdmin from './modules/relatorios/pages/RelatoriosAdmin';
import UsuariosEmpresa from './modules/usuarios/pages/UsuariosEmpresa';
import NotasFiscais from './modules/notas/pages/NotasFiscais';
import DashboardBancario from './modules/bancario/pages/DashboardBancario';
import Guias from './modules/guias/pages/Guias';
import GuiaDetalhes from './modules/guias/pages/GuiaDetalhes';
import FuncionariosPage from './modules/funcionarios/pages/FuncionariosPage';
import AdiantamentoSalarial from './modules/folha/pages/AdiantamentoSalarial';
import FolhaPagamento from './modules/folha/pages/FolhaPagamento';
import ConfiguracaoRH from './modules/folha/pages/ConfiguracaoRH';
import Holerite from './modules/folha/pages/Holerite';
import Rescisoes from './modules/rescisao/pages/Rescisoes';
import RescisaoDetalhes from './modules/rescisao/pages/RescisaoDetalhes';
import ConfiguracaoTributaria from './modules/tax/components/ConfiguracaoTributaria';
import ConfigDissidio from './modules/dissidio/pages/ConfigDissidio';
import Auditoria from './modules/auditoria/pages/Auditoria';
import ConfigurarDashboard from './modules/dashboard/pages/ConfigurarDashboard';
import ConfigurarEmpresa from './modules/empresas/pages/ConfigurarEmpresa';
import AdiantamentoDetalhes from './modules/folha/pages/AdiantamentoDetalhes';
import DarfPage from './modules/folha/pages/DarfPage';
import HomePublica from './modules/public/pages/HomePublica';
import AbrirEmpresa from './modules/public/pages/AbrirEmpresa';
import EncerrarEmpresa from './modules/public/pages/EncerrarEmpresa';
import GerenciarLeads from './modules/admin/pages/GerenciarLeads';
import AdiantamentoForm from './modules/folha/pages/AdiantamentoForm';
import {
  EditarEmpresa,
  GerenciarEmpresas,
  RegisterCompanyWizard
} from './modules/empresas';
import CriarSuperAdmin from './CriarSuperAdmin';
import DashboardAdmin from './DashboardAdmin';
import MenuSanduiche from './components/MenuSanduiche';
import './App.css';

// ============================================
// HOOK DE TEMA
// ============================================
function useTheme() {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('tema') || 'light';
  });

  React.useEffect(() => {
    if (theme === 'dark') {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('tema', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const isDark = theme === 'dark';

  return { theme, isDark, toggleTheme };
}

// ============================================
// SIDEBAR (MENU LATERAL) - APENAS DESKTOP
// ============================================
function Sidebar({ usuario, pagina, setPagina, navigate, handleLogout, menuItems, isDark, toggleTheme }) {
  const isSuperAdmin = usuario.role === 'SUPER_ADMIN';

  return (
    <aside style={{
      width: '250px',
      minHeight: '100vh',
      backgroundColor: isDark ? '#1a1a2e' : '#1a237e',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      zIndex: 100,
      boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
      overflowY: 'auto'
    }}>
      <div style={{
        padding: '20px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ fontSize: '24px' }}>
          {isSuperAdmin ? '👑' : '📊'}
        </span>
        <div>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
            {isSuperAdmin ? 'Admin' : 'Contabilidade'}
          </h2>
          <p style={{ margin: 0, fontSize: '11px', opacity: 0.7 }}>
            {isSuperAdmin ? 'Plataforma' : 'Online'}
          </p>
        </div>
      </div>

      <div style={{
        padding: '15px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          {usuario.nome_completo?.charAt(0).toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <p style={{
            margin: 0,
            fontSize: '13px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {usuario.nome_completo}
          </p>
          <p style={{
            margin: 0,
            fontSize: '11px',
            opacity: 0.7
          }}>
            {usuario.role}
          </p>
        </div>
      </div>

      <nav style={{ flex: 1, padding: '10px 0', overflowY: 'auto' }}>
        {menuItems.map((item) => {
          const isActive = pagina === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                setPagina(item.id);
                navigate(item.path);
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                color: 'white',
                border: 'none',
                borderLeft: isActive ? '4px solid #4a90e2' : '4px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.2s',
                fontWeight: isActive ? '600' : '400'
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.background = 'transparent';
              }}
            >
              {item.label}
            </button>
          );
        })}
      </nav>

      <div style={{
        padding: '15px 20px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        gap: '8px'
      }}>
        <button
          onClick={toggleTheme}
          style={{
            flex: 1,
            padding: '10px',
            background: 'rgba(255,255,255,0.1)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          {isDark ? '☀️' : '🌙'}
        </button>
        <button
          onClick={handleLogout}
          style={{
            flex: 2,
            padding: '10px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          🚪 Sair
        </button>
      </div>
    </aside>
  );
}

// ============================================
// RENDER ROUTES (função auxiliar)
// ============================================
function renderRoutes({ usuario, isDark, setPagina, navigate }) {
  const isSuperAdmin = usuario?.role === 'SUPER_ADMIN';

  return (
    <>
      <Route path="/admin" element={<DashboardAdmin isDark={isDark} />} />
      <Route path="/relatorios-admin" element={<RelatoriosAdmin isDark={isDark} />} />
      <Route path="/config-rh" element={<ConfiguracaoRH usuario={usuario} isDark={isDark} />} />
      <Route path="/folha" element={<FolhaPagamento usuario={usuario} isDark={isDark} />} />
      <Route path="/holerite/:id" element={<Holerite usuario={usuario} isDark={isDark} />} />
      <Route path="/empresas" element={<GerenciarEmpresas isDark={isDark} />} />
      <Route path="/register" element={
        <RegisterCompanyWizard
          onRegister={() => {
            setPagina('empresas');
            navigate('/empresas');
          }}
          onCancel={() => {
            setPagina('admin');
            navigate('/admin');
          }}
          isDark={isDark}
        />
      } />
      <Route path="/criar-super-admin" element={<CriarSuperAdmin isDark={isDark} />} />
      <Route path="/editar-empresa/:id" element={<EditarEmpresa isDark={isDark} />} />
      <Route path="/dashboard" element={<Dashboard usuario={usuario} isDark={isDark} />} />
      <Route path="/lancamentos" element={<Lancamentos usuario={usuario} isDark={isDark} />} />
      <Route path="/relatorios" element={<Relatorios usuario={usuario} isDark={isDark} />} />
      <Route path="/usuarios" element={<UsuariosEmpresa empresaId={usuario?.empresa_id || 1} isDark={isDark} />} />
      <Route path="/notas" element={<NotasFiscais usuario={usuario} isDark={isDark} />} />
      <Route path="/bancario" element={<DashboardBancario usuario={usuario} isDark={isDark} />} />
      <Route path="/tax-config" element={<ConfiguracaoTributaria isDark={isDark} />} />
      <Route path="/funcionarios/*" element={<FuncionariosPage usuario={usuario} isDark={isDark} />} />
      <Route path="/rescisoes" element={<Rescisoes usuario={usuario} isDark={isDark} />} />
      <Route path="/rescisao/:id" element={<RescisaoDetalhes usuario={usuario} isDark={isDark} />} />
      <Route path="/guias" element={<Guias usuario={usuario} isDark={isDark} />} />
      <Route path="/guia/:id" element={<GuiaDetalhes isDark={isDark} />} />
      <Route path="/dissidio" element={<ConfigDissidio usuario={usuario} isDark={isDark} />} />
      <Route path="/auditoria" element={<Auditoria usuario={usuario} isDark={isDark} />} />
      <Route path="/configurar-dashboard" element={<ConfigurarDashboard usuario={usuario} isDark={isDark} />} />
      <Route path="/config-empresa" element={<ConfigurarEmpresa usuario={usuario} isDark={isDark} />} />

      {/* ✅ Adiantamentos - ROTAS ESPECÍFICAS PRIMEIRO */}
      <Route path="/adiantamento/novo" element={<AdiantamentoForm usuario={usuario} isDark={isDark} />} />
      <Route path="/adiantamento/editar/:id" element={<AdiantamentoForm usuario={usuario} isDark={isDark} />} />
      <Route path="/adiantamento" element={<AdiantamentoSalarial usuario={usuario} isDark={isDark} />} />
      <Route path="/adiantamento/:id" element={<AdiantamentoDetalhes usuario={usuario} isDark={isDark} />} />

      <Route path="/darf" element={<DarfPage usuario={usuario} isDark={isDark} />} />
      <Route path="/admin/leads" element={<GerenciarLeads isDark={isDark} />} />
      
      <Route path="*" element={
        isSuperAdmin 
          ? <DashboardAdmin isDark={isDark} /> 
          : <Dashboard usuario={usuario} isDark={isDark} />
      } />
    </>
  );
}

// ============================================
// APP CONTENT
// ============================================
function AppContent() {
  const [usuario, setUsuario] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [pagina, setPagina] = useState('dashboard');
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    const user = localStorage.getItem('usuario');
    if (user) {
      setUsuario(JSON.parse(user));
    }
  }, []);

  const handleLogin = (user) => {
    setUsuario(user);
    setToken(localStorage.getItem('token'));
    setPagina(user.role === 'SUPER_ADMIN' ? 'admin' : 'dashboard');
    navigate(user.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    setUsuario(null);
    setToken(null);
    setPagina('dashboard');
    navigate('/login');
  };

  // ============================================
  // ROTAS PÚBLICAS (sem login)
  // ============================================
  const isRotaPublica = ['/', '/abrir-empresa', '/encerrar-empresa'].includes(location.pathname);

  if (isRotaPublica) {
    return (
      <Routes>
        <Route path="/" element={<HomePublica />} />
        <Route path="/abrir-empresa" element={<AbrirEmpresa />} />
        <Route path="/encerrar-empresa" element={<EncerrarEmpresa />} />
      </Routes>
    );
  }

  // ============================================
  // NÃO LOGADO → LOGIN
  // ============================================
  if (!usuario) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="*" element={<Login onLogin={handleLogin} />} />
      </Routes>
    );
  }

  // ============================================
  // SISTEMA LOGADO
  // ============================================
  const isSuperAdmin = usuario.role === 'SUPER_ADMIN';

  const menuSuperAdmin = [
    { id: 'admin', label: '👑 Dashboard', path: '/admin' },
	{ id: 'leads', label: '📥 Leads', path: '/admin/leads' },
    { id: 'relatorios-admin', label: '📄 Relatórios', path: '/relatorios-admin' },
    { id: 'empresas', label: '🏢 Empresas', path: '/empresas' },
    { id: 'register', label: '➕ Nova Empresa', path: '/register' },
    { id: 'tax-config', label: '📊 Tributário', path: '/tax-config' },
    { id: 'criar-super-admin', label: '👑 Super Admin', path: '/criar-super-admin' },
    { id: 'auditoria', label: '📋 Auditoria', path: '/auditoria' }
  ];

  const menuUsuario = [
    { id: 'dashboard', label: '📊 Dashboard', path: '/dashboard' },
    { id: 'lancamentos', label: '💰 Lançamentos', path: '/lancamentos' },
    { id: 'config-rh', label: '⚙️ RH', path: '/config-rh' },
    { id: 'adiantamento', label: '💰 Adiantamento', path: '/adiantamento' },
    { id: 'rescisoes', label: '📋 Rescisões', path: '/rescisoes' },
    { id: 'folha', label: '📄 Folha', path: '/folha' },
    { id: 'darf', label: '🧾 DARF', path: '/darf' },
    { id: 'funcionarios', label: '👥 Funcionários', path: '/funcionarios' },
    { id: 'guias', label: '📄 Guias', path: '/guias' },
    { id: 'notas', label: '📄 Notas Fiscais', path: '/notas' },
    { id: 'bancario', label: '🏦 Banco', path: '/bancario' },
    { id: 'relatorios', label: '📄 Relatórios', path: '/relatorios' },
    { id: 'usuarios', label: '👥 Usuários', path: '/usuarios' },
    { id: 'dissidio', label: '⚖️ Dissídio', path: '/dissidio' },
    { id: 'configurar-dashboard', label: '🎨 Dashboard', path: '/configurar-dashboard' },
    { id: 'config-empresa', label: '⚙️ Config. Empresa', path: '/config-empresa' }
  ];

  const menuItems = isSuperAdmin ? menuSuperAdmin : menuUsuario;

  // ============================================
  // MOBILE
  // ============================================
  if (isMobile) {
    return (
      <div className={`App ${isDark ? 'dark-mode' : ''}`} style={{
        backgroundColor: isDark ? '#121212' : '#f8f9fa',
        minHeight: '100vh',
        color: isDark ? '#e9ecef' : '#212529'
      }}>
        <header style={{
          background: isDark ? '#1a1a2e' : '#1a237e',
          padding: '10px 20px',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <MenuSanduiche
              usuario={usuario}
              pagina={pagina}
              setPagina={setPagina}
              navigate={navigate}
              handleLogout={handleLogout}
              menuItems={menuItems}
              isDark={isDark}
            />
            <h1 style={{ margin: 0, fontSize: '16px' }}>
              {isSuperAdmin ? '📊 Admin' : '📊 Contabilidade'}
            </h1>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={toggleTheme}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              {isDark ? '☀️' : '🌙'}
            </button>
          </div>
        </header>

        <main style={{ padding: '15px', maxWidth: '1400px', margin: '0 auto' }}>
          <Routes>
            {renderRoutes({ usuario, isDark, setPagina, navigate })}
          </Routes>
        </main>
      </div>
    );
  }

  // ============================================
  // DESKTOP
  // ============================================
  return (
    <div className={`App ${isDark ? 'dark-mode' : ''}`} style={{
      backgroundColor: isDark ? '#121212' : '#f8f9fa',
      minHeight: '100vh',
      color: isDark ? '#e9ecef' : '#212529'
    }}>
      <Sidebar
        usuario={usuario}
        pagina={pagina}
        setPagina={setPagina}
        navigate={navigate}
        handleLogout={handleLogout}
        menuItems={menuItems}
        isDark={isDark}
        toggleTheme={toggleTheme}
      />

      <main style={{
        marginLeft: '250px',
        padding: '20px',
        maxWidth: 'calc(100% - 250px)',
        minHeight: '100vh'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Routes>
            {renderRoutes({ usuario, isDark, setPagina, navigate })}
          </Routes>
        </div>
      </main>
    </div>
  );
}

// ============================================
// APP
// ============================================
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;